import {
  appLogout,
  injectSlice,
  State,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@betty/toolkit/state';
import { InboxMessageResponse, InboxState } from './inbox.types';
import InboxService from '../services/InboxService/InboxService';

const initialState: InboxState = {
  data: undefined,
  dataState: 'idle',
};

export const fetchInbox = createAsyncThunk('inbox/fetch', async () => {
  const messages = await InboxService.getInbox();

  // TODO: Remove Activity filter when integrating Smartico messages
  return messages.filter((message) => message.category === 'Activity');
});

export const acknowledgeInbox = createAsyncThunk(
  'inbox/acknowledgeInbox',
  (_, { getState, dispatch }) => {
    const unreadMessages = selectUnreadMessages(getState() as State);

    if (!unreadMessages?.length) {
      return;
    }

    dispatch(markInboxAsRead());

    InboxService.acknowledgeInbox({
      params: { IsRead: true, LastInboxItemId: unreadMessages[0].id },
    });
  },
);

const inbox = createSlice({
  name: 'status',
  initialState,
  reducers: {
    addInboxMessage(state, action: PayloadAction<InboxMessageResponse>) {
      // TODO: Remove Activity filter when integrating Smartico messages
      if (action.payload.category !== 'Activity') {
        state.data?.unshift(action.payload);
      }
    },
    markInboxAsRead(state) {
      state.data?.forEach((message) => {
        message.isRead = true;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInbox.pending, (state) => {
      state.dataState =
        state.dataState === 'fulfilled' ? 'refreshing' : 'pending';
    });

    builder.addCase(fetchInbox.rejected, (state) => {
      state.dataState = 'rejected';
    });

    builder.addCase(fetchInbox.fulfilled, (state, action) => {
      state.data = action.payload;
      state.dataState = 'fulfilled';
    });

    builder.addCase(appLogout, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export const { addInboxMessage, markInboxAsRead } = inbox.actions;

export const withInbox = injectSlice({
  reducerPath: 'inbox',
  reducer: inbox.reducer,
});

export const selectInbox = withInbox.selector((state) => state.inbox.data);
export const selectUnreadMessages = createSelector(selectInbox, (data) =>
  data?.filter((message) => !message.isRead),
);
export const selectInboxDataState = withInbox.selector(
  (state) => state.inbox.dataState,
);

export const selectHasUnreadInboxMessages = createSelector(
  selectInbox,
  (data) => data?.some((item) => !item.isRead),
);

declare global {
  interface LazyLoadedSlices {
    inbox: ReturnType<typeof inbox.reducer>;
  }
}
